/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-01-18 14:43:36
 *
 *******************************************************/

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Page, Layout, Icon, Tabs } from '@shopify/polaris';
import { LockMinor } from '@shopify/polaris-icons';
import { withI18n } from '@shopify/react-i18n';
import MainMenu from 'src/components/containers/main-menu';
import UpgradeSuccessBanner from 'src/components/containers/common/switchNewVersion/merchandising/upgraded-successfully-banner';
import WarningUpgradeBanner from 'src/components/containers/common/switchNewVersion/merchandising/warning-upgrade-banner';
import DidYouKnow from 'src/components/views/merchandising-v2/did-you-know';
import SortByManualBanner from 'src/components/views/merchandising-v2/sort-by-manual-banner';

import ActionsGroupOnListing from 'src/components/views/merchandising-v2/actions-group-on-listing';
import ProcessingUpdateBanner from 'src/components/views/common/processing-update-banner';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import ConfirmSwitchVersionModal from 'src/components/views/merchandising-v2/confirm-switch-version-modal';
import MerchandisingTab from './merchandising-tab';
import SearchPersonalizationPresentBanner from 'src/components/views/advanced-search/search-personalization-present-banner';
import FreemiumLockCard from 'src/components/views/merchandising-v2/lock-card';
import TitleHelp from 'src/components/views/common/title-help';
import PromoteBanner from 'src/components/views/common/promote-banner';
import { URL } from 'src/constants/links';
import { enableSubscriptionV4 } from 'src/helpers/subscription-v3-helper';
import { updateTenantApp } from 'actions/oauth-actions';
import { allowUsingNewSearchSettings } from 'src/helpers/search-settings-helper';
import { VISUAL_MERCHANDISING_NAME } from 'src/constants/subscription';
import { RELEASE_0423_DAY } from 'src/constants/campaigns.js';
import history from 'src/routes/history';

import * as merchandisingV2Actions from 'src/stores/actions/merchandising-v2-actions';
import * as merchandisingActions from 'src/stores/actions/merchandising-actions';
import * as subscriptionV3Actions from 'src/stores/actions/subscription-v3-actions';
import * as commonActions from 'src/stores/actions/common-actions';

import type { ComposedProps, State } from './index.type';

class MerchandisingV2 extends Component<ComposedProps, State> {
  timerTyping = null;
  state = {
    allMerchandisingRules: [],
    youtubeModal: false,
    switchVersionModal: false,
    queryPanel: null,
    isOpenFullScreen: false
  };

  componentWillUpdate(nextProps, nextState) {
    const { saving, deleting, scheduleSyncIn, waitSyncAt } = nextProps;
    const {
      saving: savingPrev,
      deleting: deletingPrev,
      getSyncProcessStatus,
      updateShowBanner
    } = this.props;

    // Check to show banner Processing update when change status/ import
    if ((savingPrev && !saving) || (deletingPrev && !deleting)) {
      if (waitSyncAt) {
        // Time from now to sync point
        let syncDuration = waitSyncAt - Math.floor(Date.now() / 1000);
        syncDuration = syncDuration > 0 ? syncDuration : 0;
        // Time to finish sync point
        syncDuration = Math.floor(syncDuration / 60) + scheduleSyncIn;

        updateShowBanner(syncDuration, true);
      }
      // Call sync process status
      getSyncProcessStatus();
    }
  }
  componentDidMount() {
    const { getAddonStatus, getStatusMigrate } = this.props;
    getStatusMigrate();
    getAddonStatus(VISUAL_MERCHANDISING_NAME);
    const search: string = _.get(history.location, 'search', '');
    this.checkPanelID(search);
  }

  componentWillReceiveProps(nextProps) {
    const { migrateStatus, getStatusMigrate, allowSearchV2 } = nextProps;
    const search: string = _.get(history.location, 'search', '');
    this.checkPanelID(search);

    if ((migrateStatus === 'running' || migrateStatus === 'undetermined') && !allowSearchV2) {
      clearTimeout(this.timeOutGetStatus);
      this.timeOutGetStatus = setTimeout(() => {
        getStatusMigrate();
      }, 3000);
    }
  }
  componentWillUnmount() {
    this.props.setMerchandisingTab(0);
  }
  render() {
    const {
      totalRules,
      success,
      syncDuration,
      showScheduleSync,
      allowMerchandisingV1,
      updateTenantAppLoading,
      selected,
      i18n,
      allowSearchV2,
      enableMerchandisingV2,
      migrateNewFeatureV2,
      showBannerSortRelevance,
      showBannerSortManual,
      themeSetupV2,
      enableCollectionPage,
      totalAllUnlockRules,
      activeRuleLimit,
      merchandisingV2,
      recentInstalledAt,
      migrateStatus,
      isBetaUser,
      statusStore,
      addonStatus,
      enableVisualMerchandising,
      getAddonStatus,
      isSubscriptionV4
    } = this.props;
    const addonRemainingCycles = _.get(addonStatus, 'trialInfo.remainingDays', 14);
    const isSubscribeNow =
      !['trial', 'expiredSoon'].includes(statusStore) && addonRemainingCycles <= 0;

    const { youtubeModal, switchVersionModal } = this.state;

    const getMerchandisingDocUrl = () => {
      if (isBetaUser && merchandisingV2) {
        return URL.MerchandisingDocV2;
      }

      return URL.MerchandisingDoc;
    };

    const isOldUser = moment(new Date(recentInstalledAt * 1000)).isBefore(
      new Date(RELEASE_0423_DAY)
    );

    const tabs = [
      {
        id: 'commerce',
        content: i18n.translate('AdminHTML.MerchandisingV2.Merchandising.tab_Content01'),
        panelID: 'commerce-tab'
      },
      {
        id: 'collection',
        content: (
          <p>
            {!enableCollectionPage && <Icon source={LockMinor} color="base" />}
            {i18n.translate('AdminHTML.MerchandisingV2.Merchandising.tab_Content02')}
          </p>
        ),
        panelID: 'collection-tab'
      }
    ];
    const tabComponents = [
      <MerchandisingTab
        panelID="search"
        isSubscribeNow={isSubscribeNow}
        getAddonStatus={getAddonStatus}
      />,
      <MerchandisingTab
        panelID="collection"
        getAddonStatus={getAddonStatus}
        isSubscribeNow={isSubscribeNow}
      />
    ];

    return (
      <div className="BC-Page BC-Page__MerchandisingV2">
        <MainMenu
          menuId={
            allowSearchV2
              ? `menu-${selected === 1 ? 'filters' : 'search'}-merchandising`
              : 'menu-merchandising-merchandising'
          }
        />
        {enableCollectionPage && (
          <PromoteBanner
            name="PromotePreRankingInMerchandising"
            feature="MerchandisingV2"
            learnMoreLink="Link_V2_Merchandising_MerchandisingV2_ManualGuide"
          />
        )}
        <Page
          title={i18n.translate('AdminHTML.MerchandisingV2.Merchandising.pageTitle')}
          primaryAction={
            (enableCollectionPage || tabs[selected].id !== 'collection') &&
            (totalAllUnlockRules < activeRuleLimit || !isSubscriptionV4) &&
            enableVisualMerchandising && (
              <div className="BC-Page BC-Page__Button">
                <ActionsGroupOnListing
                  isNewRule={success && totalRules === 0}
                  showButtonSwitchBack={allowMerchandisingV1}
                  onSwitchBack={() => this.toggleModal('switchVersionModal')}
                  total={totalRules}
                  isSubscribeNow={isSubscribeNow}
                  hasAddonMerchandising={enableCollectionPage}
                  getAddonStatus={getAddonStatus}
                />
              </div>
            )
          }>
          <TitleHelp
            i18n={i18n}
            onPlay={() => this.toggleModal('youtubeModal')}
            hrefUserManual={URL['Link_V2_Merchandising_MerchandisingV2_ManualGuide']}
          />
          {enableVisualMerchandising ? (
            <Layout>
              <Layout.Section>
                <div className="BC-Container__Notifications">
                  {/* Installation reminder status banner */}
                  <InstallationRemindStatusBanner />

                  {migrateStatus !== 'done' && !allowSearchV2 && (
                    <WarningUpgradeBanner
                      switchMerchandisingV2={enableMerchandisingV2}
                      migrateNewFeatureV2={migrateNewFeatureV2}
                      migrateStatus={migrateStatus}
                    />
                  )}
                  {(migrateStatus === 'done' || allowSearchV2) && isOldUser && (
                    <UpgradeSuccessBanner />
                  )}
                  {/* Upgrade successfully banner */}

                  {/* Search personalization present -> Did you know? */}
                  <SearchPersonalizationPresentBanner
                    type="horizontal"
                    page="MerchandisingV2"
                    showWhenDismissed={<DidYouKnow docUrl={getMerchandisingDocUrl()} />}
                  />

                  {/* Incase store v2 */}
                  {showBannerSortManual && showBannerSortRelevance && themeSetupV2 && (
                    <SortByManualBanner themeSetupV2={themeSetupV2} />
                  )}
                  {/* Incase store v1 */}
                  {showBannerSortManual && !themeSetupV2 && (
                    <SortByManualBanner themeSetupV2={themeSetupV2} />
                  )}

                  {/* Sticky notification for exceeded plan */}
                  <ExceededPlanNotification />

                  {/* Processing update banner */}
                  {showScheduleSync ? (
                    <ProcessingUpdateBanner
                      syncDuration={syncDuration}
                      featureName="merchandising"
                    />
                  ) : (
                    ''
                  )}
                </div>
              </Layout.Section>

              {/* List of merchandising */}
              <Layout.Section>
                <Tabs tabs={tabs} selected={selected} onSelect={this.handleTabChange}>
                  <div style={{ marginTop: '1.5rem' }} className="BC-Sync__Content">
                    {tabComponents[selected]}
                  </div>
                </Tabs>
              </Layout.Section>
            </Layout>
          ) : (
            <FreemiumLockCard />
          )}
        </Page>

        <ConfirmSwitchVersionModal
          active={switchVersionModal}
          handleSwitchVersion={this.handleSwitchVersion}
          loading={updateTenantAppLoading}
          handleCancel={() => this.toggleModal('switchVersionModal')}
        />

        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['MerchandisingTut']}
          onClose={() => this.toggleModal('youtubeModal')}
          title={i18n.translate('AdminHTML.MerchandisingV2.Merchandising.youtubeModal_Title')}
        />
      </div>
    );
  }

  // handle process panelID

  checkPanelID = (search: string) => {
    const { queryPanel } = this.state;
    const splitSearch = search.split('&');
    const panelIDString = splitSearch.find(e => e.includes('panelID'));

    // Get search query to set panelID for merchandising list
    if (!_.isEmpty(panelIDString)) {
      const ruleTypeSplit = panelIDString.split('=');
      const panelID = _.get(ruleTypeSplit, '[1]', 'search');

      // Already to set current tab
      if (panelID === queryPanel) return;

      this.props.setMerchandisingTab(panelID === 'collection' ? 1 : 0);
      this.setState({ queryPanel: panelID });

      // Remove query router after set panelID
      const removeSearch01 = search.replace('panelID=collection', '');
      const removeSearch02 = removeSearch01.replace('panelID=search', '');
      history.replace({ ...history.location, search: removeSearch02 });
    }
  };
  // Handle change tab
  handleTabChange = (selectedTabIndex: number) => {
    this.props.setMerchandisingTab(selectedTabIndex);
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  // Action back to older version
  handleSwitchVersion = () => {
    const { updateTenantApp, showFeedbackVersion } = this.props;
    const body = { 'adminAppFeatures.merchandisingV2': false };
    const meta = {
      onSuccess: () => {
        showFeedbackVersion();
      }
    };
    updateTenantApp(body, meta);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getStatusMigrate: () => dispatch(commonActions.getStatusMigrate(ownProps.i18n)),
  migrateNewFeatureV2: () => dispatch(commonActions.migrateNewFeatureV2(ownProps.i18n)),
  enableMerchandisingV2: enable =>
    dispatch(merchandisingV2Actions.enableMerchandisingV2(enable, ownProps.i18n)),
  // Get current status addon sync
  getAddonStatus: addonName => dispatch(subscriptionV3Actions.getAddonStatus(addonName)),
  updateTenantApp: (body, meta) => dispatch(updateTenantApp(body, meta, ownProps.i18n)),
  showFeedbackVersion: () => dispatch(merchandisingActions.showFeedbackVersion()),
  setMerchandisingTab: value => dispatch(merchandisingV2Actions.setMerchandisingTab(value))
});

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const recentInstalledAt = _.get(state.oauth, 'tenantApp.recentInstalledAt', '');
  const tenantId = _.get(state.oauth, 'tenantApp.tenantId', '');
  const searchVersion = _.get(state.oauth, 'tenantApp.parseParams.searchVersion', '1.0');
  const allowSearchV2 = allowUsingNewSearchSettings(searchVersion);

  return {
    tenantId,
    allowSearchV2,
    isSubscriptionV4: enableSubscriptionV4(tenantApp),
    activeRuleLimit: _.get(
      state.oauth.tenantApp,
      'settings.visualMerchandising.settings.number-rules-limit',
      100
    ),
    totalAllUnlockRules: _.get(state.merchandisingV2, 'totalAllUnlockRules', 0),
    recentInstalledAt,
    migrateStatus: _.get(state.common, 'status', null),
    statusStore: _.get(state.oauth, 'subscription.status', ''),
    addonStatus: _.get(state.subscriptionV3, `addonStatus.${VISUAL_MERCHANDISING_NAME}`, {}),
    totalRules: _.get(state.merchandisingV2, 'totalRules', 0),
    loading: _.get(state.merchandisingV2, 'loading', false),
    updateTenantAppLoading: _.get(state.oauth, 'loadingUpdate', false),
    saving: _.get(state.merchandisingV2, 'saving', false),
    deleting: _.get(state.merchandisingV2, 'deleting', false),
    success: _.get(state.merchandisingV2, 'success', false),
    selected: _.get(state.merchandisingV2, 'panelID', false),
    saveSuccess: _.get(state.merchandisingV2, 'saveSuccess', false),
    scheduleSyncIn: _.get(state.merchandisingV2, 'scheduleSyncIn', 0),
    waitSyncAt: _.get(state.merchandisingV2, 'waitSyncAt', 0),
    allowMerchandisingV1: _.get(state.oauth.tenantApp, 'allowMerchandisingV1', true),
    showBannerSortRelevance: !_.get(state.merchandisingV2, 'enableSortByRelevance', true),
    showBannerSortManual: !_.get(state.merchandisingV2, 'enableSortByManual', false),
    settings: _.get(state.oauth, 'tenantApp.settings', {}),
    merchandisingV2: _.get(tenantApp, 'adminAppFeatures.merchandisingV2', false),
    themeSetupV2: _.get(tenantApp, 'adminAppFeatures.themeSetupV2', false),
    enableCollectionPage: _.get(
      tenantApp,
      'settings.visualMerchandising.settings.collection-page',
      false
    ),
    enableVisualMerchandising: _.get(
      tenantApp,
      'settings.visualMerchandising.settings.allow-feature',
      true
    ),
    isBetaUser: _.get(tenantApp, 'settings.betaLaunchCampaign.settings.enable', false)
  };
};

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(MerchandisingV2)),
  'SyncMerchandise'
);
